import * as Yup from 'yup';
import React, { useState } from "react"
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, RHFTextField } from '../hook-form';
import 'react-multi-carousel/lib/styles.css'
import 'bootstrap/dist/css/bootstrap.css'
import './login.css'
import Iconify from '../Iconify';
import Footer from '../common/footer/Footer'
import Header from '../common/header/Header'
import { LoadingButton } from '@mui/lab';
import { Stack, IconButton, InputAdornment, Box, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { Link, useNavigate } from "react-router-dom"
import { login } from '../../api/auth';
import { ToastContainer, toast } from 'react-toastify';

/**
 * Login Component
 * Supports login via Mobile or Email
 */
const Login = () => {
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false);
  const [loginMethod, setLoginMethod] = useState('mobile'); // 'mobile' or 'email'
  const notify = (message) => toast(message)

  // Dynamic validation schema based on login method
  const LoginSchema = Yup.object().shape({
    email: loginMethod === 'email'
      ? Yup.string().email('Invalid email').required('Email is required')
      : Yup.string(),
    mobile: loginMethod === 'mobile'
      ? Yup.number().required('Mobile is required')
      : Yup.number(),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = {
    email: '',
    mobile: '',
    password: '',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset
  } = methods;

  // Handle login method change
  const handleLoginMethodChange = (event, newMethod) => {
    if (newMethod !== null) {
      setLoginMethod(newMethod);
      reset(defaultValues); // Reset form when switching
    }
  };

  const onSubmit = async (data) => {
    try {
      // Prepare login payload based on method
      const payload = {
        password: data.password,
        loginType: loginMethod
      };

      if (loginMethod === 'email') {
        payload.email = data.email;
      } else {
        payload.mobile = data.mobile;
      }

      const response = await login(payload)
      console.log(response)

      if (response?.data) {
        localStorage.setItem('token', response.data)
        notify(response.message)
        navigate('/tour')
      } else {
        notify(response.message)
      }
    } catch (error) {
      notify(error.message)
    }
  };

  return (
    <>
      <Header />
      <section className='py-5'>
        <div className='container'>
          <div className='row justify-content-center'>
            <div className='col-md-9'>
              <Box
                sx={{
                  backgroundColor: '#fff',
                  boxShadow: '0 2px 5px rgba(16, 16, 16, .09)',
                  p: 2,
                  borderRadius: '25px',
                }}
              >
                <div className='row'>
                  <div className='col-md-6'>
                    <img src='../images/signin.svg' alt="Sign in illustration" />
                  </div>
                  <div className='col-md-6'>
                    <div className='accountSection'>
                      <img src='../images/logo.jpg' alt="Logo" />
                      <h3>Welcome back</h3>
                      <p className='pb-3'>New here? <Link to='/signRegister'>Create an account</Link></p>

                      {/* Login Method Toggle */}
                      <ToggleButtonGroup
                        color="primary"
                        value={loginMethod}
                        exclusive
                        onChange={handleLoginMethodChange}
                        aria-label="Login method"
                        size="small"
                        sx={{ mb: 2, width: '100%' }}
                      >
                        <ToggleButton value="mobile" sx={{ flex: 1 }}>
                          <Iconify icon="eva:phone-fill" sx={{ mr: 1 }} />
                          Mobile
                        </ToggleButton>
                        <ToggleButton value="email" sx={{ flex: 1 }}>
                          <Iconify icon="eva:email-fill" sx={{ mr: 1 }} />
                          Email
                        </ToggleButton>
                      </ToggleButtonGroup>

                      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        {/* Conditional input based on login method */}
                        {loginMethod === 'mobile' ? (
                          <Stack sx={{ py: 1 }}>
                            <RHFTextField
                              size="small"
                              type='number'
                              name="mobile"
                              label="Enter mobile number"
                            />
                          </Stack>
                        ) : (
                          <Stack sx={{ py: 1 }}>
                            <RHFTextField
                              size="small"
                              type='email'
                              name="email"
                              label="Enter email address"
                            />
                          </Stack>
                        )}

                        <Stack sx={{ py: 2 }}>
                          <RHFTextField
                            name="password"
                            label="Password"
                            size="small"
                            type={showPassword ? 'text' : 'password'}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton edge="end" onClick={() => setShowPassword(!showPassword)}>
                                    <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Stack>
                        <Stack sx={{ py: 1, textAlign: 'end' }}>
                          <Link to='/forgotPassword' className="">Forgot password?</Link>
                        </Stack>
                        <Stack sx={{ py: 1 }}>
                          <LoadingButton fullWidth size="large" type="submit" className='signBtn' loading={isSubmitting}>
                            Login with {loginMethod === 'email' ? 'Email' : 'Mobile'}
                          </LoadingButton>
                        </Stack>
                      </FormProvider>
                    </div>
                  </div>
                </div>
              </Box>
            </div>
          </div>
        </div>
      </section>
      <ToastContainer />
      <Footer />
    </>
  )
}

export default Login
